import type { UIMessage } from "ai";

import type {
  RepositoryChatCitation,
  RepositoryChatConversation,
  RepositoryChatMessage,
  RepositoryChatTurn,
} from "../../dashboard-client";

import { formatRelativeTime } from "../../utils/time";

export type RepositoryChatRetrievalMode =
  | "hybrid"
  | "indexed_first"
  | "indexed_only"
  | "live_only";

export interface RetrievalStatus {
  message: string;
  status:
    | "pending"
    | "queued"
    | "running"
    | "succeeded"
    | "failed"
    | "timed_out";
  taskId: null | string;
  turnId: string;
}

export interface CitationData {
  citations: RepositoryChatCitation[];
  consultedRepos: string[];
  freshnessByRepo: Record<string, string>;
  turnId: string;
}

export interface ChatMessageData {
  final: boolean;
  messageId: string;
  parts: readonly { content: string; type: "text" }[];
  paused: boolean;
  role: "assistant";
}

interface RepositoryChatDataParts {
  "chat:message": ChatMessageData;
  "repo-citation": CitationData;
  "retrieval-status": RetrievalStatus;
  [key: string]: ChatMessageData | CitationData | RetrievalStatus;
}

export type RepositoryChatUiMessage = UIMessage<never, RepositoryChatDataParts>;

export interface RepositoryChatTurnStatusSnapshot {
  consultedRepos: string[];
  status: RetrievalStatus["status"];
  taskId: null | string;
  turnId: string;
  updatedAt: string;
}

export const RETRIEVAL_MODE_OPTIONS: {
  description: string;
  label: string;
  value: RepositoryChatRetrievalMode;
}[] = [
  {
    description: "Blend indexed snippets with live repository reads.",
    label: "Hybrid",
    value: "hybrid",
  },
  {
    description: "Prefer indexed context, then fall back to live reads.",
    label: "Indexed First",
    value: "indexed_first",
  },
  {
    description: "Only use indexed repository evidence.",
    label: "Indexed Only",
    value: "indexed_only",
  },
  {
    description: "Skip the index and read the repository live.",
    label: "Live Only",
    value: "live_only",
  },
];

export function getConversationTitle(
  conversation: RepositoryChatConversation,
): string {
  if (conversation.primaryRepositorySlug) {
    return conversation.primaryRepositorySlug;
  }

  if (conversation.repositoryScope.length === 1) {
    return conversation.repositoryScope[0] ?? "Repository chat";
  }

  return `${conversation.repositoryScope.length} repositories`;
}

export function getLatestTurnStatusSnapshot(
  conversation: RepositoryChatConversation,
): RepositoryChatTurnStatusSnapshot | null {
  const turns = (conversation.messages ?? []).flatMap(
    (message) => message.turns ?? [],
  );
  const latestTurn =
    getMostRecentBusyTurn(turns) ?? getMostRecentlyUpdatedTurn(turns);

  if (!latestTurn) {
    return null;
  }

  return {
    consultedRepos: latestTurn.consultedRepos ?? [],
    status: latestTurn.status,
    taskId: latestTurn.retrievalTaskId,
    turnId: latestTurn.turnId,
    updatedAt: latestTurn.updatedAt,
  };
}

export function toRetrievalStatusMessage(
  status: RetrievalStatus["status"],
): string {
  switch (status) {
    case "failed": {
      return "Repository retrieval failed.";
    }
    case "pending": {
      return "Repository retrieval is preparing.";
    }
    case "queued": {
      return "Repository retrieval is queued.";
    }
    case "running": {
      return "Repository retrieval is running.";
    }
    case "succeeded": {
      return "Repository retrieval completed.";
    }
    case "timed_out": {
      return "Repository retrieval timed out.";
    }
  }
}

export function toTurnRetrievalStatus(
  snapshot: RepositoryChatTurnStatusSnapshot | null,
): RetrievalStatus | null {
  if (!snapshot) {
    return null;
  }

  return {
    message: toRetrievalStatusMessage(snapshot.status),
    status: snapshot.status,
    taskId: snapshot.taskId,
    turnId: snapshot.turnId,
  };
}

export function mergeRetrievalStatuses({
  liveStatus,
  snapshot,
}: {
  liveStatus: RetrievalStatus | null;
  snapshot: RepositoryChatTurnStatusSnapshot | null;
}): RetrievalStatus | null {
  const snapshotStatus = toTurnRetrievalStatus(snapshot);
  if (!liveStatus) {
    return snapshotStatus;
  }

  if (!snapshotStatus) {
    return liveStatus;
  }

  if (snapshotStatus.turnId !== liveStatus.turnId) {
    return liveStatus;
  }

  const preferredStatus =
    getStatusRank(snapshotStatus.status) >= getStatusRank(liveStatus.status)
      ? snapshotStatus
      : liveStatus;

  return {
    message:
      preferredStatus === snapshotStatus
        ? toRetrievalStatusMessage(snapshotStatus.status)
        : liveStatus.message,
    status: preferredStatus.status,
    taskId:
      preferredStatus.taskId ?? snapshotStatus.taskId ?? liveStatus.taskId,
    turnId: preferredStatus.turnId,
  };
}

export function getConversationSubtitle(
  conversation: RepositoryChatConversation,
): string {
  const repoCount = conversation.repositoryScope.length;
  const repoLabel =
    repoCount === 1 ? "1 repository" : `${repoCount} repositories`;
  const updatedAt = formatIsoRelativeTime(conversation.updatedAt);
  return `${repoLabel} · Updated ${updatedAt}`;
}

export function formatIsoRelativeTime(value: string): string {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return "recently";
  }

  return formatRelativeTime(timestamp);
}

export function toRepositoryChatUiMessages(
  messages: RepositoryChatConversation["messages"] | undefined,
): RepositoryChatUiMessage[] {
  return (messages ?? [])
    .filter(
      (
        message,
      ): message is RepositoryChatMessage & {
        role: "assistant" | "system" | "user";
      } => message.role !== "data",
    )
    .map((message) => ({
      id: message.messageId,
      parts: toUiMessageParts(message),
      role: message.role,
    }));
}

export function renderRepositoryChatMessageText(
  message: RepositoryChatUiMessage,
): string {
  return message.parts
    .filter(
      (
        part,
      ): part is Extract<(typeof message.parts)[number], { type: "text" }> =>
        part.type === "text",
    )
    .map((part) => part.text)
    .join("");
}

export function getCitationDataParts(
  parts: RepositoryChatUiMessage["parts"],
): CitationData[] {
  return parts.flatMap((part) => {
    if (part.type !== "data-repo-citation" || !("data" in part)) {
      return [];
    }

    return isCitationData(part.data) ? [part.data] : [];
  });
}

export function isChatMessageData(value: unknown): value is ChatMessageData {
  return (
    typeof value === "object" &&
    value !== null &&
    "parts" in value &&
    "role" in value &&
    "final" in value &&
    "paused" in value &&
    "messageId" in value &&
    Array.isArray(value.parts) &&
    typeof value.final === "boolean" &&
    typeof value.paused === "boolean" &&
    typeof value.messageId === "string" &&
    value.role === "assistant"
  );
}

export function isRetrievalStatus(value: unknown): value is RetrievalStatus {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    "status" in value &&
    "taskId" in value &&
    "turnId" in value &&
    typeof value.message === "string" &&
    typeof value.status === "string" &&
    (typeof value.taskId === "string" || value.taskId === null) &&
    typeof value.turnId === "string"
  );
}

function getStatusRank(status: RetrievalStatus["status"]): number {
  switch (status) {
    case "pending": {
      return 0;
    }
    case "queued": {
      return 1;
    }
    case "running": {
      return 2;
    }
    case "succeeded": {
      return 3;
    }
    case "failed": {
      return 3;
    }
    case "timed_out": {
      return 3;
    }
  }
}

function getMostRecentBusyTurn(turns: readonly RepositoryChatTurn[]) {
  return turns
    .filter((turn) => isBusyRetrievalStatus(turn.status))
    .toSorted(compareTurnsByCreatedAtDesc)[0];
}

function getMostRecentlyUpdatedTurn(turns: readonly RepositoryChatTurn[]) {
  return turns.toSorted(compareTurnsByUpdatedAtDesc)[0];
}

function compareTurnsByCreatedAtDesc(
  left: RepositoryChatTurn,
  right: RepositoryChatTurn,
) {
  return Date.parse(right.createdAt) - Date.parse(left.createdAt);
}

function compareTurnsByUpdatedAtDesc(
  left: RepositoryChatTurn,
  right: RepositoryChatTurn,
) {
  return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
}

function isBusyRetrievalStatus(status: RetrievalStatus["status"]): boolean {
  return status === "pending" || status === "queued" || status === "running";
}

function isCitationData(
  value: RepositoryChatDataParts[string],
): value is CitationData {
  return "citations" in value;
}

function toUiMessageParts(
  message: RepositoryChatMessage & { role: "assistant" | "system" | "user" },
): RepositoryChatUiMessage["parts"] {
  const parts: RepositoryChatUiMessage["parts"] = [];

  if (message.content.trim()) {
    parts.push({ text: message.content, type: "text" });
  }

  if (message.role === "assistant") {
    for (const turn of message.turns ?? []) {
      if ((turn.citations ?? []).length === 0) {
        continue;
      }

      parts.push({
        data: {
          citations: turn.citations ?? [],
          consultedRepos: turn.consultedRepos ?? [],
          freshnessByRepo: turn.freshnessByRepo ?? {},
          turnId: turn.turnId,
        },
        type: "data-repo-citation",
      });
    }
  }

  return parts;
}
